/**
 * Realtime patch for the site scheduling calendar.
 *
 * The odl_app 'calendar' contract broadcasts the whole event series on every
 * calendar write. This handler merges those series into the slice (so the event
 * dialog sees fresh recurrence descriptors) and debounced-refetches the visible
 * occurrence window (so the grid reflects the new schedule). It applies the two
 * guards the contract requires: a pre-seed no-op (drop events before the calendar
 * has fetched a window, since there is nothing to refresh yet) and a monotonic
 * generated_at check (never act on an emit older than the last one applied).
 *
 * Contains:
 * - handleCalendarSocketActions — the 'calendar' model socket handler
 */
import { fetchCalendarOccurrences } from '@sophocles/features/calendar/api';
import { resolveCalendarEmit } from '@sophocles/features/calendar/calendarSocketDecision';
import { upsertSeries } from '@sophocles/features/calendar/slice';

const REFETCH_DEBOUNCE_MS = 300;

let refetchTimer = null;

/**
 * @param {'+' | '-'} _op  - unused (calendar is always a whole-list snapshot)
 * @param {{ events?: object[] }} payload - the calendar contract data ({events})
 * @param {{ siteId: number|string, generatedAt: ?string }} meta - envelope metadata
 */
export const handleCalendarSocketActions = (_op, payload, meta) => (dispatch, getState) => {
  const plan = resolveCalendarEmit(payload, meta, getState().calendar);
  if (!plan) {
    return;
  }

  dispatch(upsertSeries({ events: plan.events, generatedAt: plan.generatedAt }));

  clearTimeout(refetchTimer);
  refetchTimer = setTimeout(() => {
    dispatch(fetchCalendarOccurrences({ siteId: plan.siteId, start: plan.window.start, end: plan.window.end }));
  }, REFETCH_DEBOUNCE_MS);
};
